import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Section } from "@/components/marketing/Section";

export { CONTACT_EMAIL } from "@/lib/brand";

// No legal entity has been formed yet, so the docs name "the Mavori team" as
// the operator. Once the entity exists (likely a Delaware C-Corp), swap ENTITY
// for its registered name and confirm JURISDICTION matches the state of
// incorporation.
export const ENTITY = "the Mavori team";
export const JURISDICTION = "the State of Delaware, USA";

// Update when the policy text materially changes. Surfaces in the "Last
// updated" line on each legal page.
export const LAST_UPDATED = "2026-08-02";

export function LegalShell({
  title,
  children,
  lastUpdated = LAST_UPDATED,
}: {
  title: string;
  children: ReactNode;
  lastUpdated?: string;
}) {
  const { t } = useTranslation();
  return (
    <Section>
      <article className="prose prose-invert max-w-3xl">
        <h1 className="font-sans font-semibold text-display text-foreground">{title}</h1>
        <p className="mt-1 font-mono text-xs uppercase tracking-widest text-subtle-foreground">
          {t("legal.lastUpdated", { date: lastUpdated })} · {t("legal.englishOnly")}
        </p>
        <div className="mt-8 flex flex-col gap-6">{children}</div>
      </article>
    </Section>
  );
}

export function LegalH2({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <h2 id={id} className="mt-2 scroll-mt-24 font-sans font-semibold text-2xl text-foreground">
      {children}
    </h2>
  );
}

export function LegalP({ children }: { children: ReactNode }) {
  return <p className="leading-relaxed text-muted-foreground">{children}</p>;
}

export function LegalEmphasis({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-card border border-border bg-card/60 px-5 py-4">
      <p className="text-base font-semibold leading-relaxed text-foreground">{children}</p>
    </div>
  );
}

// `items` is typed as `string[]` (not `ReactNode[]`) so each bullet's text
// is also its React key. Legal documents only ever pass plain prose, and
// constraining the prop keeps biome's noArrayIndexKey happy without an
// ignore comment — the string content is the stable identity here.
export function LegalBullets({ items }: { items: string[] }) {
  return (
    <ul className="ml-5 flex list-disc flex-col gap-1.5 text-muted-foreground">
      {items.map((item) => (
        <li key={item} className="leading-relaxed">
          {item}
        </li>
      ))}
    </ul>
  );
}
